import {
  ContactInfo,
  ExtendedShareholderDTO,
  Gender,
  getGenderLabel,
  PersonInfo,
  ShareholderField,
} from "../../types/Shareholder";
import {
  contactInfoNameValidator,
  formatOrgNum,
  personNameValidator,
  phoneValidatorAllowingEmpty,
  utlNumberValidator,
} from "../../utils/other";
import { formatPhoneNumberToE164 } from "../../utils/phoneNumbers";
import { makeAutoObservable } from "mobx";
import { Address, emailValidator } from "../../common/common";
import { idnr } from "@navikt/fnrvalidator";
import { isEmpty, isFinite } from "lodash";
import { getFullName } from "../../utils/Roles";
import { lookupNorwegianCity } from "../../utils/postalCodes";

import { isValidDate, toYearISOString } from "../../utils/Dates";
import { getBooleanLabel } from "./editSingleShareholderDomain";
import { Language } from "../../i18n/languages";
import { TFunction, TMessage } from "../../i18n/tt";
import { ParsedShareholder, ParsedShareholderData } from "../../types/excelImportDomain";
import { norway } from "../../utils/countries";
import {
  LocalDateFieldModel,
  NumberFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "../../common/commonForms";
import { isBlank } from "../../utils/strings";

export class ShareholderModel {
  id: number;
  nameModel: StringFieldModel;
  nationalityModel: StringFieldModel;
  contactInfoNameModel: StringFieldModel;
  contactInfoNationalIdModel: StringFieldModel;
  emailModel: StringFieldModel;
  phoneModel: StringFieldModel;
  nationalIdModel: StringFieldModel;
  companyNumberModel: StringFieldModel;
  addressModel: StringFieldModel;
  postalCodeModel: StringFieldModel;
  cityModel: StringFieldModel;
  countryModel: StringFieldModel;
  genderModel: RadioFieldModel<Gender | null>;
  isEmployeeModel: RadioFieldModel<boolean | null>;
  utlNumberModel: StringFieldModel;
  lockupEndDateModel: LocalDateFieldModel;
  loanAmountModel: NumberFieldModel;
  issuedShareholderCertificate: StringFieldModel;
  bicModel: StringFieldModel;
  ibanModel: StringFieldModel;
  bankDetailsCountryModel: StringFieldModel;
  readonly data: ExtendedShareholderDTO;
  shareholderId: number;
  companyNumber: string | undefined;
  contactInfo?: ContactInfo;
  personInfo?: PersonInfo;
  shareholderAddress: Address;
  isCompany: boolean;
  isValid = true;

  constructor(
    data: ExtendedShareholderDTO,
    readonly hasShares: boolean,
  ) {
    this.data = data;
    this.id = data.id;
    this.companyNumber = data.birthDateOrOrganisationNumber;
    this.shareholderId = data.id;
    this.contactInfo = data.contactInfo;
    this.personInfo = data.personInfo;
    this.shareholderAddress = data.address ?? {};
    this.isCompany = data.isCompany;
    this.nameModel = new StringFieldModel({
      validator: data.isCompany ? undefined : personNameValidator,
      required: true,
      value: data.isCompany ? data.companyName : getFullName(data.personInfo),
    });
    this.contactInfoNameModel = new StringFieldModel({
      validator: data.isCompany ? contactInfoNameValidator : undefined,
      value: data.isCompany ? getFullName(data.contactInfo) : "",
    });
    this.contactInfoNationalIdModel = new StringFieldModel({
      value: data.contactInfo?.socialSecurityNumber,
    });
    const personInfo = this.isCompany ? data.contactInfo : data.personInfo;
    this.emailModel = new StringFieldModel({
      validator: emailValidator,
      value: personInfo?.email,
    });
    this.phoneModel = new StringFieldModel({
      validator: phoneValidatorAllowingEmpty,
      value: formatPhoneNumberToE164({
        value: personInfo?.phoneNumber?.value ?? "",
        countryCode: personInfo?.phoneNumber?.countryCode ?? "",
      }),
    });

    this.nationalIdModel = new StringFieldModel({
      value: data.personInfo?.socialSecurityNumber,
    });
    this.nationalityModel = new StringFieldModel({
      required: true,
      value: data.nationality ?? Language.NO.toUpperCase(),
    });
    this.companyNumberModel = new StringFieldModel({
      value: data.companyNumber,
    });
    this.addressModel = new StringFieldModel({
      value: data.address?.address,
    });
    this.postalCodeModel = new StringFieldModel({
      validator: postalCodeValidator(() => this.countryModel.validatedValue),
      value: data.address?.postalCode,
    });
    this.cityModel = new StringFieldModel({
      value: data.address?.city,
    });
    this.countryModel = new StringFieldModel({
      required: true,
      value: data.address?.country,
    });
    this.genderModel = new RadioFieldModel<Gender | null>({
      value: (data.isCompany ? data.contactInfo?.gender : data.personInfo?.gender) || null,
      options: [null, "male", "female"],
      idFn: (value) => String(value),
      labelFn: (value) => getGenderLabel(value),
    });
    this.isEmployeeModel = new RadioFieldModel<boolean | null>({
      value: data.isEmployee === undefined ? null : data.isEmployee,
      options: [null, true, false],
      idFn: (value) => String(value),
      labelFn: (value) => getBooleanLabel(value),
    });
    this.utlNumberModel = new StringFieldModel({
      value: data.utlNumber,
      validator: utlNumberValidator,
    });
    this.lockupEndDateModel = new LocalDateFieldModel({
      value: data.lockupEndDate,
    });
    this.loanAmountModel = new NumberFieldModel({
      value: data.loanAmount,
      isPositiveOrZero: true,
    });
    this.issuedShareholderCertificate = new StringFieldModel({
      value: String(data.issuedShareholderCertificate),
    });
    this.bicModel = new StringFieldModel({
      value: data.bic,
    });
    this.ibanModel = new StringFieldModel({
      value: data.iban,
    });
    this.bankDetailsCountryModel = new StringFieldModel({
      value: data.bankDetailsCountry,
    });
    makeAutoObservable(this);
  }

  /*
  The law says:
  (2) I aksjeeierboken skal aksjeeierne innføres i alfabetisk orden med angivelse av navn, fødselsdato, digital adresse og bostedsadresse,
      eller – for juridiske personer – foretaksnavn, organisasjonsnummer eller lignende identifikasjonsnummer, digital adresse, forretningsadresse
      og eventuelt postadresse.
  (3) For hver aksjeeier skal det angis antall aksjer og nummer på aksjene. Har selskapet flere aksjeklasser, skal det angis hvilken klasse aksjene tilhører.
   */
  validate(shareholders: ShareholderModel[] = []): ValidationResultForShareholder {
    const common: ValidationResultForField[] = [
      {
        field: ShareholderField.shareholderName,
        isValid: this.nameModel.validate(),
        error: this.nameModel.error,
      },
      {
        field: ShareholderField.email,
        isValid: this.emailModel.validate(),
        error: this.emailModel.error,
      },
      {
        field: ShareholderField.phone,
        isValid: this.phoneModel.validate(),
        error: this.phoneModel.error,
      },
      {
        field: ShareholderField.address,
        isValid: this.addressModel.validate(),
        error: this.addressModel.error,
      },
      {
        field: ShareholderField.postalCode,
        isValid: this.postalCodeModel.validate(),
        error: this.postalCodeModel.error,
      },
      {
        field: ShareholderField.city,
        isValid: this.cityModel.validate(),
        error: this.cityModel.error,
      },
      {
        field: ShareholderField.country,
        isValid: this.countryModel.validate(),
        error: this.countryModel.error,
      },
    ];

    const isCompanyNumberValid =
      this.isCompany &&
      this.companyNumberModel.validate() &&
      this.isValidCompanyNumber() &&
      !this.isDuplicateCompanyNumber(shareholders);

    const companyValidations: ValidationResultForField[] = [
      {
        field: ShareholderField.companyNumber,
        isValid: isCompanyNumberValid,
        error: this.companyNumberModel.error,
      },
      {
        field: ShareholderField.contactInfoName,
        isValid: this.contactInfoNameModel.validate(),
        error: this.contactInfoNameModel.error,
      },
    ];

    const isNationalIdValid =
      !this.isCompany &&
      this.nationalIdModel.validate() &&
      this.validateNationalId() &&
      this.validateDuplicateNationalId(shareholders);
    const personValidations: ValidationResultForField[] = [
      {
        field: ShareholderField.nationalId,
        isValid: isNationalIdValid,
        error: this.nationalIdModel.error,
      },
    ];
    const specific = this.isCompany ? companyValidations : personValidations;
    const validations: ValidationResultForField[] = [...common, ...specific];
    this.isValid = validations.every((v) => v.isValid);
    return { isValid: this.isValid, validationForFields: validations };
  }

  isDuplicateCompanyNumber(shareholders: ShareholderModel[]) {
    const isDuplicate = shareholders.some(
      (s) =>
        s.shareholderId !== this.shareholderId &&
        !isEmpty(s.companyNumberModel.inputValue) &&
        !isEmpty(this.companyNumberModel.inputValue) &&
        s.companyNumberModel.inputValue === this.companyNumberModel.inputValue,
    );
    if (isDuplicate) {
      this.companyNumberModel.setError({ key: "nonUniqueCompanyNumber" });
    }
    return isDuplicate;
  }

  validateDuplicateNationalId(shareholders: ShareholderModel[]) {
    const isDuplicate = shareholders.some(
      (s) =>
        s.shareholderId !== this.shareholderId &&
        !isEmpty(s.nationalIdModel.inputValue) &&
        !isEmpty(this.nationalIdModel.inputValue) &&
        s.nationalIdModel.inputValue === this.nationalIdModel.inputValue,
    );
    if (isDuplicate) {
      this.nationalIdModel.setError({ key: "nonUniqueSocialSecurityNumber" });
    }
    return !isDuplicate;
  }

  get isSwedishShareholder(): boolean {
    return this.nationalityModel.inputValue === "SE";
  }

  get isNorwegianShareholder(): boolean {
    return this.nationalityModel.inputValue === norway;
  }

  isValidSwedishNationalId(): boolean {
    return isValidSwedishNationalId(
      this.nationalIdModel.inputValue,
      this.nationalityModel.inputValue,
    );
  }

  isValidNorwegianNationalId(): boolean {
    return this.isNorwegianShareholder && idnr(this.nationalIdModel.inputValue).status === "valid";
  }

  validateNationalId(): boolean {
    if (!this.isSwedishShareholder && !this.isNorwegianShareholder) {
      return true;
    }
    const nationality = this.nationalityModel.inputValue;
    const isValid = this.isValidSwedishNationalId() || this.isValidNorwegianNationalId();
    if (!isValid) {
      this.nationalIdModel.setError({
        key: nationality === "SE" ? "invalidSwedishNationalId" : "invalidNorwegianNationalId",
      });
    }
    return isValid;
  }

  isValidCompanyNumber(): boolean {
    const isValid =
      this.nationalityModel.inputValue !== norway ||
      this.companyNumberModel.validatedValue.length === 9;
    if (!isValid) {
      this.companyNumberModel.setError({ key: "invalidNorwegianOrgNo" });
    }
    return isValid;
  }

  prepareImportData(parsedShareholderData: ParsedShareholderData[]): ParsedShareholder {
    const parsedShareholder = parsedShareholderData.find((s) => s.row.id === this.id);
    return {
      id: this.id,
      type: this.isCompany ? "selskap" : "person",
      name: this.nameModel.inputValue,
      identification: this.isCompany
        ? this.companyNumberModel.inputValue
        : this.nationalIdModel.inputValue,
      representativeName: this.contactInfoNameModel.inputValue,
      representativeSsn: this.contactInfoNationalIdModel.inputValue,
      address: this.addressModel.inputValue,
      postalCode: this.postalCodeModel.inputValue,
      city: this.cityModel.inputValue,
      country: this.countryModel.inputValue,
      email: this.emailModel.inputValue,
      phoneNumber: this.phoneModel.inputValue,
      utlNumber: this.utlNumberModel.inputValue,
      lockupEndDate: this.lockupEndDateModel.inputValue,
      loanAmount: this.loanAmountModel.parsedValue?.toString() ?? "",
      gender: this.genderModel.value?.toString() ?? "",
      isEmployee: this.isEmployeeModel.value === true ? "y" : "",
      numberOfShares: parsedShareholder?.row?.numberOfShares ?? "",
      shareNumbers: parsedShareholder?.row?.shareNumbers ?? "",
      entryDate: parsedShareholder?.row?.entryDate ?? "",
      numberOfPreferenceShares: parsedShareholder?.row?.numberOfPreferenceShares ?? "",
      numberOfAShares: parsedShareholder?.row?.numberOfAShares ?? "",
      numberOfBShares: parsedShareholder?.row?.numberOfBShares ?? "",
      numberOfCShares: parsedShareholder?.row?.numberOfCShares ?? "",
    };
  }
}

export type ValidationResultForShareholder = {
  isValid: boolean;
  validationForFields: ValidationResultForField[];
};

export type ValidationResultForField = {
  field: ShareholderField;
  isValid: boolean;
  error: TMessage | undefined;
};

export type ValidationResultForShareholderRegister = {
  isValid: boolean;
  validationsForShareholders: ValidationResultForShareholder[];
};

export function validateAllShareholders(
  shareholders: ShareholderModel[],
): ValidationResultForShareholderRegister {
  const validations: ValidationResultForShareholder[] = shareholders.map((s) =>
    s.validate(shareholders),
  );
  return {
    isValid: validations.every((v) => v.isValid),
    validationsForShareholders: validations,
  };
}

export function isValidSwedishNationalId(nationalId: string, isoCountryCode: string): boolean {
  const isSwedish = isoCountryCode === "SE";
  if (!isSwedish) {
    return false;
  }
  if (isUtlNumber(nationalId)) {
    return true;
  }
  if (isBlank(nationalId)) {
    return true;
  }
  return (
    isFinite(Number(nationalId)) &&
    nationalId.length === 12 &&
    isValidDate(nationalId.substring(0, 8))
  );
}

const isUtlNumber = (input: string): boolean => input.length === 9;

export const postalCodeValidator = (countryProvider: () => string) => {
  return (value: string): TMessage | undefined => {
    if (value.trim().length === 0) {
      return undefined;
    } else if (countryProvider() === norway && lookupNorwegianCity(value) === undefined) {
      return { key: "invalidPostalCode" };
    } else {
      return undefined;
    }
  };
};

export const formatBirthYearOrOrgNo = (
  t: TFunction,
  shareholder: ExtendedShareholderDTO,
): string => {
  if (!shareholder.birthDateOrOrganisationNumber) {
    return "";
  } else {
    return shareholder.isCompany
      ? t("transactions.buyer-seller.description.orgNo", {
          orgNo: formatOrgNum(shareholder.birthDateOrOrganisationNumber),
        })
      : t("transactions.buyer-seller.description.born", {
          year: toYearISOString(shareholder.birthDateOrOrganisationNumber || ""),
        });
  }
};
